import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Book, ChevronLeft, ChevronRight, Award, RefreshCw, Clock, Home, CheckCircle, XCircle, AlertTriangle, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QuizQuestion from '../components/QuizQuestion';
import QuizResult from '../components/QuizResult';
import { getPointsPerQuestion, getQuizRankingTitle, shuffleArray } from '../lib/quiz';

interface Category {
  id: string;
  name: string;
  min_age: number;
  max_age: number | null;
}

interface Question {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  difficulty: string;
  explanation?: string;
}

interface QuizResult {
  id: string;
  score: number;
  total_questions: number;
  completed_at: string;
}

export default function BibleQuiz() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedAgeCategory, setSelectedAgeCategory] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [previousResults, setPreviousResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [showCustomQuiz, setShowCustomQuiz] = useState(false);
  const navigate = useNavigate();

  // Lista de perguntas do quiz personalizado
  const customQuestions: Question[] = [
    {
      id: "custom1",
      question: "Qual foi o primeiro milagre realizado por Jesus segundo o Evangelho de João?",
      options: [
        "Multiplicação dos pães e peixes",
        "Cura de um leproso",
        "Transformação de água em vinho",
        "Ressurreição de Lázaro"
      ],
      correct_answer: 2,
      difficulty: "medium",
      explanation: "Em João 2:1-11, Jesus transformou água em vinho durante um casamento em Caná da Galileia, sendo este seu primeiro milagre registrado."
    },
    {
      id: "custom2",
      question: "Quem foi o rei que pediu sabedoria a Deus em vez de riquezas?",
      options: [
        "Davi",
        "Salomão",
        "Ezequias",
        "Josias"
      ],
      correct_answer: 1,
      difficulty: "medium",
      explanation: "Em 1 Reis 3:5-14, Salomão pediu a Deus um coração entendido para julgar o povo, e Deus ficou tão satisfeito que lhe deu sabedoria e também riquezas."
    },
    {
      id: "custom3",
      question: "Qual profeta foi levado ao céu em um redemoinho?",
      options: [
        "Eliseu",
        "Elias",
        "Isaías",
        "Jeremias"
      ],
      correct_answer: 1,
      difficulty: "medium",
      explanation: "Em 2 Reis 2:11, Elias foi arrebatado ao céu em um redemoinho, enquanto Eliseu, seu discípulo, observava."
    },
    {
      id: "custom4",
      question: "Qual dos seguintes livros não está no Antigo Testamento?",
      options: [
        "Provérbios",
        "Eclesiastes",
        "Filipenses",
        "Lamentações"
      ],
      correct_answer: 2,
      difficulty: "medium",
      explanation: "Filipenses é uma carta escrita pelo apóstolo Paulo e faz parte do Novo Testamento. Os outros livros mencionados pertencem ao Antigo Testamento."
    },
    {
      id: "custom5",
      question: "Quem foi jogado na cova dos leões por continuar orando a Deus?",
      options: [
        "Daniel",
        "Jeremias",
        "Ezequiel",
        "Jonas"
      ],
      correct_answer: 0,
      difficulty: "medium",
      explanation: "Em Daniel 6, o profeta Daniel foi lançado na cova dos leões por continuar orando a Deus, desobedecendo ao decreto do rei Dario."
    },
    {
      id: "custom6",
      question: "Qual discípulo duvidou da ressurreição de Jesus até ver suas feridas?",
      options: [
        "Pedro",
        "João",
        "Tomé",
        "Tiago"
      ],
      correct_answer: 2,
      difficulty: "medium",
      explanation: "Em João 20:24-29, Tomé duvidou da ressurreição de Jesus e disse que só acreditaria se pudesse ver e tocar suas feridas."
    },
    {
      id: "custom7",
      question: "Qual era a profissão de Paulo antes de se tornar apóstolo?",
      options: [
        "Pescador",
        "Médico",
        "Fariseu",
        "Carpinteiro"
      ],
      correct_answer: 2,
      difficulty: "medium",
      explanation: "Paulo era um fariseu, educado aos pés de Gamaliel, como ele mesmo afirma em Atos 23:6 e Filipenses 3:5."
    },
    {
      id: "custom8",
      question: "Qual foi a primeira mulher mencionada na Bíblia?",
      options: [
        "Sara",
        "Eva",
        "Maria",
        "Rebeca"
      ],
      correct_answer: 1,
      difficulty: "medium",
      explanation: "Eva foi a primeira mulher criada por Deus, formada da costela de Adão, conforme descrito em Gênesis 2:21-22."
    },
    {
      id: "custom9",
      question: "Quantos livros compõem o Novo Testamento?",
      options: [
        "39",
        "27",
        "12",
        "46"
      ],
      correct_answer: 1,
      difficulty: "medium",
      explanation: "O Novo Testamento é composto por 27 livros, incluindo os quatro evangelhos, Atos dos Apóstolos, as epístolas e o Apocalipse."
    },
    {
      id: "custom10",
      question: "Qual o nome do monte onde Moisés recebeu os Dez Mandamentos?",
      options: [
        "Monte Sinai",
        "Monte Carmelo",
        "Monte das Oliveiras",
        "Monte Tabor"
      ],
      correct_answer: 0,
      difficulty: "medium",
      explanation: "Moisés recebeu os Dez Mandamentos no Monte Sinai, como registrado em Êxodo 19-20."
    }
  ];

  useEffect(() => {
    fetchCategories();
    fetchPreviousResults();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timerActive && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      handleTimeUp();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, timerActive]);

  async function fetchCategories() {
    try {
      const { data, error } = await supabase
        .from('quiz_categories')
        .select('*')
        .order('min_age');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchQuestions(categoryId: string) {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('category_id', categoryId)
        .eq('difficulty', difficulty)
        .order('created_at');

      if (error) throw error;
      
      // Limitar o número de questões com base na dificuldade
      const questionCount = difficulty === 'easy' ? 10 : difficulty === 'medium' ? 15 : 20;
      const limitedQuestions = data?.slice(0, questionCount) || [];
      
      // Embaralhar as questões
      const shuffledQuestions = shuffleArray(limitedQuestions);
      
      setQuestions(shuffledQuestions);
      setAnswers(new Array(shuffledQuestions.length).fill(null));
      setCurrentQuestionIndex(0);
      setScore(0);
      setQuizCompleted(false);
      setTimeLeft(30);
      setTimerActive(true);
      setShowAnswer(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  }

  function startCustomQuiz() {
    // Embaralhar as perguntas do quiz personalizado
    const shuffledQuestions = shuffleArray([...customQuestions]);
    setQuestions(shuffledQuestions);
    setAnswers(new Array(shuffledQuestions.length).fill(null));
    setCurrentQuestionIndex(0);
    setScore(0);
    setQuizCompleted(false);
    setTimeLeft(30);
    setTimerActive(true);
    setShowAnswer(false);
    setShowCustomQuiz(true);
  }

  async function fetchPreviousResults() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('quiz_results')
        .select('*')
        .eq('user_id', session.user.id)
        .order('completed_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPreviousResults(data || []);
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  }

  async function saveResult() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from('quiz_results')
        .insert({
          user_id: session.user.id,
          category_id: selectedCategory,
          score,
          total_questions: questions.length,
        });

      if (error) throw error;
      await fetchPreviousResults();
    } catch (error) {
      console.error('Error saving result:', error);
    }
  }

  function handleAnswer(answerIndex: number) {
    if (showAnswer) return; // Não permitir mudar a resposta após revelar a resposta correta
    
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setAnswers(newAnswers);
    setSelectedAnswer(answerIndex);
    setShowAnswer(true);
    setTimerActive(false);
    
    // Atualizar pontuação se a resposta estiver correta
    if (answerIndex === questions[currentQuestionIndex].correct_answer) {
      const pointsPerQuestion = showCustomQuiz ? 10 : getPointsPerQuestion(difficulty);
      setScore(score + pointsPerQuestion);
    }
  }

  function handleTimeUp() {
    setTimerActive(false);
    setShowAnswer(true);
    
    // Marcar como resposta incorreta
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = -1; // -1 indica que o tempo acabou
    setAnswers(newAnswers);
  }

  function handleNext() {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(answers[currentQuestionIndex + 1]);
      setShowAnswer(false);
      setTimeLeft(30);
      setTimerActive(true);
    } else {
      setQuizCompleted(true);
      if (!showCustomQuiz) {
        saveResult();
      }
    }
  }

  function handlePrevious() {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedAnswer(answers[currentQuestionIndex - 1]);
      setShowAnswer(true); // Já respondida, então mostrar resposta
      setTimerActive(false);
    }
  }

  function restartQuiz() {
    setAnswers(new Array(questions.length).fill(null));
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setScore(0);
    setQuizCompleted(false);
    setTimeLeft(30);
    setTimerActive(true);
    setShowAnswer(false);
  }

  function resetQuiz() {
    setSelectedCategory('');
    setSelectedAgeCategory('');
    setShowCustomQuiz(false);
    setQuizCompleted(false);
  }

  function getTotalPossiblePoints(): number {
    if (showCustomQuiz) {
      return questions.length * 10; // 10 pontos por questão no quiz personalizado
    } else {
      const pointsPerQuestion = getPointsPerQuestion(difficulty);
      return questions.length * pointsPerQuestion;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Book className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Quiz Bíblico</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      {!selectedCategory && !showCustomQuiz ? (
        <div className="bg-white p-8 rounded-xl shadow-elevation-1">
          <h2 className="text-xl font-semibold text-brand-teal mb-6">
            Bem-vindo ao Quiz Bíblico!
          </h2>
          <p className="text-gray-600 mb-6">
            Teste seus conhecimentos bíblicos respondendo a perguntas sobre personagens, 
            histórias e ensinamentos da Bíblia. Escolha uma das opções abaixo para começar.
          </p>
          
          {/* Opção de Quiz Interativo */}
          <div className="mb-8 p-6 bg-brand-light rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="h-6 w-6 text-brand-teal" />
              <h3 className="text-lg font-semibold text-brand-teal">Quiz Interativo</h3>
            </div>
            <p className="text-gray-700 mb-4">
              10 perguntas desafiadoras sobre conhecimentos bíblicos diversos. 
              Cada resposta correta vale 10 pontos, totalizando 100 pontos possíveis.
            </p>
            <button
              onClick={startCustomQuiz}
              className="w-full bg-brand-teal text-white px-6 py-3 rounded-lg hover:bg-brand-teal/90 transition-colors"
            >
              Iniciar Quiz Interativo
            </button>
          </div>
          
          {/* Opções de Quiz por Categoria */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Book className="h-6 w-6 text-brand-teal" />
              <h3 className="text-lg font-semibold text-brand-teal">Quiz por Categoria</h3>
            </div>
            
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Escolha o nível de dificuldade:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficulty === 'easy'
                      ? 'border-brand-teal bg-brand-light text-brand-teal'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium mb-1">Iniciante</div>
                  <div className="text-sm text-gray-600">
                    10 questões, 5 pontos cada
                  </div>
                </button>
                
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficulty === 'medium'
                      ? 'border-brand-teal bg-brand-light text-brand-teal'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium mb-1">Intermediário</div>
                  <div className="text-sm text-gray-600">
                    15 questões, 10 pontos cada
                  </div>
                </button>
                
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficulty === 'hard'
                      ? 'border-brand-teal bg-brand-light text-brand-teal'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium mb-1">Avançado</div>
                  <div className="text-sm text-gray-600">
                    20 questões, 15 pontos cada
                  </div>
                </button>
              </div>
            </div>
            
            <h3 className="font-medium text-gray-900 mb-3">Escolha sua faixa etária:</h3>
            <div className="space-y-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedAgeCategory(category.id);
                    setSelectedCategory(category.id);
                    fetchQuestions(category.id);
                  }}
                  className="w-full p-4 text-left bg-brand-light hover:bg-brand-light/80 text-brand-teal rounded-lg transition-colors"
                >
                  <span className="font-medium">{category.name}</span>
                  <p className="text-sm text-brand-teal/70">
                    {category.max_age
                      ? `${category.min_age} a ${category.max_age} anos`
                      : `${category.min_age}+ anos`}
                  </p>
                </button>
              ))}
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Regras do jogo:</h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-600">
              <li>Cada pergunta tem 4 alternativas</li>
              <li>Apenas uma resposta é correta</li>
              <li>Tempo máximo de 30 segundos por questão</li>
              <li>Não é permitido consultar a Bíblia durante o quiz</li>
              <li>Ao final, você receberá sua pontuação total e ranking</li>
            </ul>
          </div>
        </div>
      ) : !quizCompleted ? (
        <div className="bg-white p-8 rounded-xl shadow-elevation-1">
          <div className="flex justify-between items-center mb-6">
            <span className="text-sm text-gray-500">
              Pergunta {currentQuestionIndex + 1} de {questions.length}
            </span>
            <div className="h-2 flex-1 mx-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-teal transition-all duration-300"
                style={{
                  width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                }}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className={`${timeLeft <= 5 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                {timeLeft}s
              </span>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {questions[currentQuestionIndex]?.question}
            </h2>
            <div className="space-y-3">
              {questions[currentQuestionIndex]?.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={showAnswer}
                  className={`w-full p-4 text-left rounded-lg transition-colors flex justify-between items-center ${
                    showAnswer
                      ? index === questions[currentQuestionIndex].correct_answer
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : selectedAnswer === index
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-gray-50 text-gray-500'
                      : selectedAnswer === index
                      ? 'bg-brand-teal text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span>{option}</span>
                  {showAnswer && (
                    index === questions[currentQuestionIndex].correct_answer ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : selectedAnswer === index ? (
                      <XCircle className="h-5 w-5 text-red-600" />
                    ) : null
                  )}
                </button>
              ))}
            </div>
          </div>

          {showAnswer && (
            <div className="mb-6 p-4 bg-brand-light rounded-lg">
              <h3 className="font-medium text-brand-teal mb-2">Explicação:</h3>
              <p className="text-gray-700">
                {questions[currentQuestionIndex]?.explanation || 
                  (selectedAnswer === questions[currentQuestionIndex]?.correct_answer
                    ? "Parabéns! Você acertou a resposta."
                    : `A resposta correta é: ${
                        questions[currentQuestionIndex]?.options[
                          questions[currentQuestionIndex]?.correct_answer
                        ]
                      }`)}
              </p>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="px-4 py-2 flex items-center gap-2 text-gray-600 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </button>
            <button
              onClick={handleNext}
              disabled={!showAnswer}
              className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {currentQuestionIndex === questions.length - 1 ? (
                'Finalizar'
              ) : (
                <>
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-xl shadow-elevation-1">
          <div className="text-center mb-8">
            <div className="inline-block p-4 rounded-full bg-brand-light mb-4">
              <Award className="h-12 w-12 text-brand-teal" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Quiz Concluído!
            </h2>
            <p className="text-gray-600">
              Você acertou {answers.filter((a, i) => a === questions[i]?.correct_answer).length} de {questions.length} questões
            </p>
            <div className="mt-4">
              <div className="text-2xl font-bold text-brand-teal">
                {score} pontos de {getTotalPossiblePoints()}
              </div>
              <p className="text-lg font-medium text-gray-700 mt-2">
                {Math.round((score / getTotalPossiblePoints()) * 100)}% de acertos
              </p>
              <p className="text-xl font-semibold text-brand-teal mt-4">
                {getQuizRankingTitle(Math.round((score / getTotalPossiblePoints()) * 100))}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="p-4 bg-brand-light rounded-lg">
              <h3 className="font-medium text-brand-teal mb-2">Estatísticas</h3>
              <ul className="space-y-2 text-gray-700">
                <li>Total de questões: {questions.length}</li>
                <li>Acertos: {answers.filter((a, i) => a === questions[i]?.correct_answer).length}</li>
                <li>Erros: {answers.filter((a, i) => a !== null && a !== questions[i]?.correct_answer).length}</li>
                <li>Pontuação: {score} de {getTotalPossiblePoints()} pontos</li>
              </ul>
            </div>
            
            <div className="p-4 bg-brand-light rounded-lg">
              <h3 className="font-medium text-brand-teal mb-2">Avaliação</h3>
              <p className="text-gray-700 mb-2">
                {Math.round((score / getTotalPossiblePoints()) * 100) >= 70
                  ? "Excelente trabalho! Você demonstra um bom conhecimento bíblico."
                  : Math.round((score / getTotalPossiblePoints()) * 100) >= 50
                  ? "Bom trabalho! Continue estudando a Palavra."
                  : "Continue estudando a Palavra para melhorar seu conhecimento bíblico."}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-600">
                  Lembre-se: o conhecimento deve ser aplicado à vida!
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={restartQuiz}
              className="flex-1 bg-brand-teal text-white px-6 py-3 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-5 w-5" />
              Tentar Novamente
            </button>
            
            <button
              onClick={resetQuiz}
              className="flex-1 border border-brand-teal text-brand-teal px-6 py-3 rounded-lg hover:bg-brand-light transition-colors"
            >
              Escolher Outro Quiz
            </button>
          </div>

          {!showCustomQuiz && previousResults.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Histórico de Resultados
              </h3>
              <div className="space-y-3">
                {previousResults.map((result) => (
                  <div
                    key={result.id}
                    className="p-4 bg-gray-50 rounded-lg flex justify-between items-center"
                  >
                    <div>
                      <p className="text-sm text-gray-500">
                        {new Date(result.completed_at).toLocaleDateString('pt-BR')}
                      </p>
                      <p className="font-medium">
                        {result.score} de {result.total_questions * getPointsPerQuestion(difficulty)} pontos
                      </p>
                    </div>
                    <div className="text-lg font-bold text-brand-teal">
                      {Math.round((result.score / (result.total_questions * getPointsPerQuestion(difficulty))) * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}